/**
 * Ceiling Step Component
 *
 * Supports three modes:
 * - Standard (FullRoom): Toggle ceiling on/off and select color
 * - CeilingOnly: Multi-zone ceiling editor with presets/custom dimensions
 * - IndividualWalls: Ceiling dimensions configuration when ceiling is included
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstimator } from '../../../context/EstimatorContext';
import { useToast } from '../../../context/ToastContext';
import { ProductColor, CeilingZone } from '../../../types';
import { trackCeilingConfigured, trackCeilingZoneConfigured, trackZoneAdded } from '../../../utils/gtmTracking';
import { ROOM_PRESETS, SPACE_TYPES } from '../../../data/presets';
import { LEGACY_UI_ICONS, WALL_PANEL_IMAGES } from '../../../data/productImages';
import { Ceiling3DViewer } from '../../ui/Ceiling3DViewer';
import { DimensionInput, UnitToggle } from '../../ui/DimensionInput';
import { useUnitMode } from '../../../hooks/useUnitMode';
import {
  Square,
  Plus,
  Trash2,
  Copy,
  LayoutTemplate,
  Pencil,
  Maximize,
  ArrowLeftRight,
  Move,
  Info,
  Car,
  Home,
  WashingMachine,
  Hammer,
  Warehouse,
  Building2,
  LayoutGrid,
  Bath,
} from 'lucide-react';
import './Steps.css';

const spaceTypeIconMap: Record<string, typeof Car> = {
  Car,
  Home,
  WashingMachine,
  Hammer,
  Warehouse,
  Building2,
  LayoutGrid,
  Bath,
};

// Ceiling dimension presets (same as room presets but without height)
const CEILING_PRESETS = ROOM_PRESETS.map(p => ({
  id: p.id,
  length: p.length,
  width: p.width,
}));

/**
 * Length + Width entry for one ceiling zone. Owns its own ft/in <-> decimal
 * toggle (per-group, persisted) and renders the standard centered row used by
 * all three ceiling modes. Values stay decimal-feet numbers.
 */
const ZoneDimensionFields: React.FC<{
  unitKey: string;
  length: number;
  width: number;
  onChange: (field: 'length' | 'width', value: number) => void;
  lengthId?: string;
  widthId?: string;
  lengthFieldId?: string;
  widthFieldId?: string;
}> = ({ unitKey, length, width, onChange, lengthId, widthId, lengthFieldId, widthFieldId }) => {
  const { t } = useTranslation('wizard');
  const [mode, setMode] = useUnitMode(unitKey);
  const lengthError = length < 1 || length > 200;
  const widthError = width < 1 || width > 200;
  return (
    <div className="dim-group">
      <div className="row g-5 justify-content-center">
        <div className="col-auto">
          <div className="d-flex flex-column align-items-center">
            <label htmlFor={lengthId} className="field-label text-nowrap mb-2 d-inline-flex align-items-center gap-1">
              <ArrowLeftRight size={16} />
              {t('shared.fieldLength')}
            </label>
            <DimensionInput
              id={lengthId}
              fieldId={lengthFieldId}
              value={length}
              onChange={v => onChange('length', v)}
              mode={mode}
              min={1}
              max={200}
              hasError={lengthError}
              ariaLabel={t('shared.lengthAria')}
            />
            {lengthError && <span className="field-hint error text-center">{t('shared.mustBe1to200')}</span>}
          </div>
        </div>

        <div className="col-auto d-none d-sm-flex align-items-center px-0">
          <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
        </div>

        <div className="col-auto">
          <div className="d-flex flex-column align-items-center">
            <label htmlFor={widthId} className="field-label text-nowrap mb-2 d-inline-flex align-items-center gap-1">
              <Move size={16} />
              {t('shared.fieldWidth')}
            </label>
            <DimensionInput
              id={widthId}
              fieldId={widthFieldId}
              value={width}
              onChange={v => onChange('width', v)}
              mode={mode}
              min={1}
              max={200}
              hasError={widthError}
              ariaLabel={t('shared.widthAria')}
            />
            {widthError && <span className="field-hint error text-center">{t('shared.mustBe1to200')}</span>}
          </div>
        </div>
      </div>

      <UnitToggle mode={mode} onChange={setMode} />
    </div>
  );
};

export const CeilingStep: React.FC = () => {
  const {
    state,
    updateCeilingConfig,
    addCeilingZone,
    copyCeilingZone,
    updateCeilingZone,
    removeCeilingZone,
    updateApplicationConfig,
  } = useEstimator();
  const { showToast } = useToast();
  const { t } = useTranslation(['wizard', 'common']);
  const { ceilingConfig, calculationMode, applicationConfig } = state;
  const [showLightbox, setShowLightbox] = useState(false);

  // Derive zone modes from global state (persisted across navigation)
  const zoneModes: Record<string, 'preset' | 'custom'> = {};
  for (const zone of (ceilingConfig.zones || [])) {
    zoneModes[zone.id] = zone.usePreset === false ? 'custom' : 'preset';
  }
  // Ceiling dimension mode for IndividualWalls (persisted in global state)
  const ceilingDimensionMode = ceilingConfig.dimensionUsePreset === false ? 'custom' : 'preset';

  // Get step number for tracking (ceiling step position depends on mode)
  const ceilingStepNumber = calculationMode === 'CeilingOnly' ? 2 : 3;

  const handleCeilingIncludeChange = (include: boolean) => {
    updateCeilingConfig({ include });
    trackCeilingConfigured(calculationMode, include, include ? ceilingConfig.color : null, ceilingStepNumber);
  };

  const handleColorChange = (color: ProductColor) => {
    updateCeilingConfig({ color });
    // Full Room / Individual Walls paths: this is the unified ceiling color
    // picker. Ceiling Only uses per-zone handlers (trackCeilingZoneConfigured).
    trackCeilingConfigured(calculationMode, ceilingConfig.include, color, ceilingStepNumber);
  };

  // GTM: debounced, gate-until-complete tracking for
  // `estimator_ceiling_zone_configured`. Only fires once a zone has every
  // required field filled (length + width > 0; color always has a default).
  // Keyed by zone index so edits to different zones don't clobber each other.
  const zoneTrackTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = zoneTrackTimersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const scheduleZoneConfiguredEvent = (index: number, zone: CeilingZone, totalZones: number) => {
    const timers = zoneTrackTimersRef.current;
    const existing = timers.get(index);
    if (existing) clearTimeout(existing);

    const complete = zone.length > 0 && zone.width > 0;
    if (!complete) {
      timers.delete(index);
      return;
    }

    const timer = setTimeout(() => {
      trackCeilingZoneConfigured(
        index,
        zone.name,
        String(zone.color),
        zone.length,
        zone.width,
        totalZones,
      );
      timers.delete(index);
    }, 500);

    timers.set(index, timer);
  };

  // Zone handlers for CeilingOnly mode
  const handleZoneChange = (index: number, field: keyof CeilingZone, value: any) => {
    updateCeilingZone(index, { [field]: value });
    if (['color', 'length', 'width'].includes(field)) {
      const zones = ceilingConfig.zones || [];
      const zone = zones[index];
      if (zone) {
        const u = { ...zone, [field]: value } as CeilingZone;
        scheduleZoneConfiguredEvent(index, u, zones.length);
      }
    }
  };

  const handleZoneModeChange = (zoneId: string, mode: 'preset' | 'custom') => {
    const zones = ceilingConfig.zones || [];
    const zoneIndex = zones.findIndex(z => z.id === zoneId);
    if (zoneIndex >= 0) {
      updateCeilingZone(zoneIndex, { usePreset: mode === 'preset' });
    }
  };

  const handleZonePresetSelect = (index: number, presetId: string) => {
    const preset = CEILING_PRESETS.find(p => p.id === presetId);
    if (preset) {
      updateCeilingZone(index, {
        length: preset.length,
        width: preset.width,
      });
    }
  };

  // Render multi-zone editor for CeilingOnly mode
  if (calculationMode === 'CeilingOnly') {
    const zones = ceilingConfig.zones || [];

    return (
      <div className="step-content">
        <div className="step-header">
          <h2 className="step-title">{t('ceiling.zonesTitle')}</h2>
        </div>

        <div className="form-section">
          <h3 className="section-title">
            <Building2 size={20} className="section-icon" />
            {t('shared.spaceType')}
          </h3>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            {SPACE_TYPES.map(type => {
              const IconComponent = spaceTypeIconMap[type.icon];
              return (
                <button
                  key={type.id}
                  type="button"
                  className={`icon-select-btn icon-select-sm ${
                    applicationConfig.spaceType === type.id ? 'selected' : ''
                  }`}
                  onClick={() => updateApplicationConfig({ spaceType: type.id })}
                >
                  <div className="icon-select-img">
                    {IconComponent && <IconComponent size={32} strokeWidth={1.5} />}
                  </div>
                  <span className="icon-select-label">{t(`spaceTypes.${type.id}`)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="step-header">
          <p className="step-description">
            {t('ceiling.zonesIntro')}
          </p>
        </div>

        <div className="zones-editor">
          {zones.map((zone, index) => {
            const zoneMode = zoneModes[zone.id] || 'preset';

            return (
              <div key={zone.id} className="zone-card">
                <div className="zone-card-header">
                  <h3 className="zone-card-title">
                    <input
                      type="text"
                      value={zone.name}
                      onChange={(e) => handleZoneChange(index, 'name', e.target.value)}
                      className="field-input"
                      style={{ width: '150px', fontWeight: 600 }}
                      data-field-id={`zone-${index}-name`}
                    />
                  </h3>
                  <div className="wall-card-actions">
                    <button
                      type="button"
                      className="wall-card-copy"
                      onClick={() => {
                        if (!zone.name?.trim()) {
                          showToast(t('ceiling.enterZoneName'), `zone-${index}-name`);
                          return;
                        }
                        copyCeilingZone(index);
                      }}
                      title={!zone.name?.trim() ? t('ceiling.fixZoneName') : t('ceiling.copyZone')}
                    >
                      <Copy size={16} />
                      {t('shared.copy')}
                    </button>
                    {zones.length > 1 && (
                      <button
                        type="button"
                        className="wall-card-remove"
                        onClick={() => removeCeilingZone(index)}
                      >
                        <Trash2 size={16} />
                        {t('shared.remove')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Preset vs Custom Toggle */}
                <div className="mode-toggle compact">
                  <button
                    type="button"
                    className={`mode-button ${zoneMode === 'preset' ? 'active' : ''}`}
                    onClick={() => handleZoneModeChange(zone.id, 'preset')}
                  >
                    <LayoutTemplate size={14} />
                    {t('shared.presets')}
                  </button>
                  <button
                    type="button"
                    className={`mode-button ${zoneMode === 'custom' ? 'active' : ''}`}
                    onClick={() => handleZoneModeChange(zone.id, 'custom')}
                  >
                    <Pencil size={14} />
                    {t('shared.custom')}
                  </button>
                </div>

                {/* Preset Selection */}
                {zoneMode === 'preset' && (
                  <div className="ceiling-presets-grid justify-content-center">
                    {CEILING_PRESETS.map(preset => (
                      <button
                        key={preset.id}
                        type="button"
                        className={`preset-card compact ${
                          zone.length === preset.length && zone.width === preset.width ? 'selected' : ''
                        }`}
                        onClick={() => handleZonePresetSelect(index, preset.id)}
                      >
                        <Maximize size={16} className="preset-icon" />
                        <span className="preset-name">{t('ceiling.presetSize', { length: preset.length, width: preset.width })}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Custom Dimensions */}
                {zoneMode === 'custom' && (
                  <>
                    <ZoneDimensionFields
                      unitKey={`ceiling-zone-${index}`}
                      length={zone.length}
                      width={zone.width}
                      onChange={(f, v) => handleZoneChange(index, f, v)}
                      lengthFieldId={`zone-${index}-length`}
                      widthFieldId={`zone-${index}-width`}
                    />

                    {/* Ceiling joists measurement guide */}
                    <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-3" role="alert">
                      <div className="flex-shrink-0 text-info">
                        <Info size={20} />
                      </div>
                      <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                        <p className="mb-0 small">
                          {t('shared.joistNote')}
                        </p>
                        <div className="text-center flex-shrink-0">
                          <img
                            src={LEGACY_UI_ICONS.dimensionsGuide}
                            alt={t('shared.joistImageAlt')}
                            style={{ maxWidth: '200px', cursor: 'pointer' }}
                            onClick={() => setShowLightbox(true)}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <span className="d-block text-muted mt-1" style={{ cursor: 'pointer', fontSize: '0.65rem' }} onClick={() => setShowLightbox(true)}>
                            {t('shared.clickFullSize')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Color Selection with Product Images */}
                <div className="zone-color-section">
                  <h3 className="section-title">{t('ceiling.zoneColor')}</h3>
                  <div className="color-selector with-images">
                    <button
                      type="button"
                      className={`icon-select-btn ${zone.color === 'White' ? 'selected' : ''}`}
                      onClick={() => handleZoneChange(index, 'color', 'White')}
                    >
                      <div className="icon-select-img">
                        <img
                          src={WALL_PANEL_IMAGES.White.thumbnail}
                          alt={t('common:productAlt.wcb', { color: t('common:colors.White') })}
                          className="product-image"
                        />
                      </div>
                      <span className="icon-select-label">{t('common:colors.White')}</span>
                    </button>
                    <button
                      type="button"
                      className={`icon-select-btn ${zone.color === 'Gray' ? 'selected' : ''}`}
                      onClick={() => handleZoneChange(index, 'color', 'Gray')}
                    >
                      <div className="icon-select-img">
                        <img
                          src={WALL_PANEL_IMAGES.Gray.thumbnail}
                          alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })}
                          className="product-image"
                        />
                      </div>
                      <span className="icon-select-label">{t('common:colors.Gray')}</span>
                    </button>
                  </div>
                </div>

                {/* Zone Area Summary */}
                <div className="zone-area-summary">
                  <Square size={16} />
                  <span>{t('ceiling.area')} <strong>{t('shared.squareFeet', { value: (zone.length * zone.width).toLocaleString() })}</strong></span>
                </div>

                {/* 3D Ceiling Viewer */}
                <div className="zone-diagram">
                  <Ceiling3DViewer
                    length={zone.length}
                    width={zone.width}
                    color={zone.color}
                    compact={true}
                  />
                </div>
              </div>
            );
          })}

          <button type="button" className="add-zone-button" onClick={() => { addCeilingZone(); trackZoneAdded((ceilingConfig.zones?.length || 0) + 1); }}>
            <Plus size={20} />
            {t('ceiling.addZone')}
          </button>
        </div>

        {/* Summary */}
        <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
          <div className="flex-shrink-0 text-info">
            <Info size={20} />
          </div>
          <div>
            <strong>{t('ceiling.zonesConfigured', { count: zones.length })}</strong>
            <p className="mb-0 small mt-1">
              {t('ceiling.totalArea', { value: zones.reduce((sum, z) => sum + z.length * z.width, 0).toLocaleString() })}
            </p>
          </div>
        </div>

        {showLightbox && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center lightbox-overlay"
            style={{ zIndex: 9999, cursor: 'pointer' }}
            onClick={() => setShowLightbox(false)}
          >
            <img src={LEGACY_UI_ICONS.dimensionsGuide} alt={t('shared.joistImageAlt')} className="lightbox-image" />
          </div>
        )}
      </div>
    );
  }

  // IndividualWalls mode: Show ceiling dimensions configuration when ceiling is included
  if (calculationMode === 'IndividualWalls') {
    const zones = ceilingConfig.zones || [];
    const mainZone = zones[0];

    return (
      <div className="step-content">
        <div className="step-header">
          <h2 className="step-title">{t('ceiling.title')}</h2>
          <p className="step-description">
            {t('ceiling.descriptionIndividual')}
          </p>
        </div>

        {/* Ceiling Included - Image-based selector */}
        <div className="form-section">
          <h3 className="section-title">{t('ceiling.included')}</h3>
          <div className="ceiling-image-selector">
            <button
              type="button"
              className={`icon-select-btn ${!ceilingConfig.include ? 'selected' : ''}`}
              onClick={() => handleCeilingIncludeChange(false)}
            >
              <div className="icon-select-img">
                <img
                  src={LEGACY_UI_ICONS.wallsOnly}
                  alt={t('ceiling.wallsOnlyAlt')}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <span className="icon-select-label">{t('shared.no')}</span>
            </button>

            <button
              type="button"
              className={`icon-select-btn ${ceilingConfig.include ? 'selected' : ''}`}
              onClick={() => handleCeilingIncludeChange(true)}
            >
              <div className="icon-select-img">
                <img
                  src={LEGACY_UI_ICONS.wallsAndCeiling}
                  alt={t('ceiling.wallsAndCeilingAlt')}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <span className="icon-select-label">{t('shared.yes')}</span>
            </button>
          </div>
        </div>

        {/* Ceiling Configuration (only if ceiling is included) */}
        {ceilingConfig.include && (
          <>
            {/* Ceiling Color Selection */}
            <div className="form-section">
              <h3 className="section-title">{t('ceiling.panelColor')}</h3>
              <div className="color-selector with-images">
                <button
                  type="button"
                  className={`icon-select-btn ${ceilingConfig.color === 'White' ? 'selected' : ''}`}
                  onClick={() => handleColorChange('White')}
                >
                  <div className="icon-select-img">
                    <img
                      src={WALL_PANEL_IMAGES.White.thumbnail}
                      alt={t('common:productAlt.wcb', { color: t('common:colors.White') })}
                      className="product-image"
                    />
                  </div>
                  <span className="icon-select-label">{t('common:colors.White')}</span>
                </button>
                <button
                  type="button"
                  className={`icon-select-btn ${ceilingConfig.color === 'Gray' ? 'selected' : ''}`}
                  onClick={() => handleColorChange('Gray')}
                >
                  <div className="icon-select-img">
                    <img
                      src={WALL_PANEL_IMAGES.Gray.thumbnail}
                      alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })}
                      className="product-image"
                    />
                  </div>
                  <span className="icon-select-label">{t('common:colors.Gray')}</span>
                </button>
              </div>
            </div>

            {/* Ceiling Dimensions */}
            <div className="form-section">
              <h3 className="section-title">{t('ceiling.dimensions')}</h3>

              {/* Zone name + actions */}
              {mainZone && (
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <input
                    type="text"
                    value={mainZone.name}
                    onChange={(e) => updateCeilingZone(0, { name: e.target.value })}
                    className="field-input"
                    style={{ width: '150px', fontWeight: 600 }}
                    data-field-id="zone-0-name"
                  />
                  <div className="wall-card-actions">
                    <button
                      type="button"
                      className="wall-card-copy"
                      onClick={() => {
                        if (!mainZone.name?.trim()) {
                          showToast(t('ceiling.enterZoneName'), 'zone-0-name');
                          return;
                        }
                        copyCeilingZone(0);
                      }}
                      title={t('ceiling.copyZone')}
                    >
                      <Copy size={16} />
                      {t('shared.copy')}
                    </button>
                  </div>
                </div>
              )}

              {/* Preset vs Custom Toggle */}
              <div className="mode-toggle">
                <button
                  type="button"
                  className={`mode-button ${ceilingDimensionMode === 'preset' ? 'active' : ''}`}
                  onClick={() => updateCeilingConfig({ dimensionUsePreset: true })}
                >
                  <LayoutTemplate size={16} />
                  {t('shared.usePresets')}
                </button>
                <button
                  type="button"
                  className={`mode-button ${ceilingDimensionMode === 'custom' ? 'active' : ''}`}
                  onClick={() => updateCeilingConfig({ dimensionUsePreset: false })}
                >
                  <Pencil size={16} />
                  {t('shared.customDimensions')}
                </button>
              </div>

              {/* Preset Selection */}
              {ceilingDimensionMode === 'preset' && (
                <div className="presets-grid" style={{ marginTop: '16px' }}>
                  {CEILING_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`preset-card ${
                        mainZone && mainZone.length === preset.length && mainZone.width === preset.width ? 'selected' : ''
                      }`}
                      onClick={() => {
                        if (mainZone) {
                          updateCeilingZone(0, { length: preset.length, width: preset.width });
                        } else {
                          // Initialize with a new zone
                          addCeilingZone();
                          // The zone will be created, update it on next render
                        }
                      }}
                    >
                      <Maximize size={20} className="preset-icon" />
                      <span className="preset-name">{t('ceiling.presetSize', { length: preset.length, width: preset.width })}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Custom Dimensions */}
              {ceilingDimensionMode === 'custom' && (
                <>
                  <ZoneDimensionFields
                    unitKey="ceiling-main"
                    length={mainZone?.length || 20}
                    width={mainZone?.width || 12}
                    onChange={(f, v) => {
                      if (mainZone) updateCeilingZone(0, f === 'length' ? { length: v } : { width: v });
                    }}
                    lengthId="ceiling-length"
                    widthId="ceiling-width"
                    lengthFieldId="zone-0-length"
                    widthFieldId="zone-0-width"
                  />

                  {/* Ceiling joists measurement guide */}
                  <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
                    <div className="flex-shrink-0 text-info">
                      <Info size={20} />
                    </div>
                    <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                      <p className="mb-0 small">
                        {t('shared.joistNote')}
                      </p>
                      <div className="text-center flex-shrink-0">
                        <img
                          src={LEGACY_UI_ICONS.dimensionsGuide}
                          alt={t('shared.joistImageAlt')}
                          style={{ maxWidth: '200px', cursor: 'pointer' }}
                          onClick={() => setShowLightbox(true)}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="d-block text-muted mt-1" style={{ cursor: 'pointer', fontSize: '0.65rem' }} onClick={() => setShowLightbox(true)}>
                          {t('shared.clickFullSize')}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Area Summary */}
              {mainZone && (
                <div className="zone-area-summary" style={{ marginTop: '16px' }}>
                  <Square size={16} />
                  <span>{t('ceiling.area')} <strong>{t('shared.squareFeet', { value: (mainZone.length * mainZone.width).toLocaleString() })}</strong></span>
                </div>
              )}

              {/* 3D Ceiling Viewer */}
              {mainZone && (
                <div className="zone-diagram" style={{ marginTop: '16px' }}>
                  <Ceiling3DViewer
                    length={mainZone.length}
                    width={mainZone.width}
                    color={mainZone.color}
                  />
                </div>
              )}

              {/* Additional Zones (for L-shaped layouts) */}
              {zones.length > 1 && (
                <div className="additional-zones-section" style={{ marginTop: '24px' }}>
                  <h4 className="subsection-label">{t('ceiling.additionalZones')}</h4>

                  {zones.slice(1).map((zone, idx) => {
                    const actualIndex = idx + 1;
                    const zoneMode = zoneModes[zone.id] || 'preset';

                    return (
                      <div key={zone.id} className="zone-card">
                        {/* Name + Actions */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <input
                            type="text"
                            value={zone.name}
                            onChange={(e) => handleZoneChange(actualIndex, 'name', e.target.value)}
                            className="field-input"
                            style={{ width: '150px', fontWeight: 600 }}
                            data-field-id={`zone-${actualIndex}-name`}
                          />
                          <div className="wall-card-actions">
                            <button
                              type="button"
                              className="wall-card-copy"
                              onClick={() => {
                                if (!zone.name?.trim()) {
                                  showToast(t('ceiling.enterZoneName'), `zone-${actualIndex}-name`);
                                  return;
                                }
                                copyCeilingZone(actualIndex);
                              }}
                              title={!zone.name?.trim() ? t('ceiling.fixZoneName') : t('ceiling.copyZone')}
                            >
                              <Copy size={16} />
                              {t('shared.copy')}
                            </button>
                            <button
                              type="button"
                              className="wall-card-remove"
                              onClick={() => removeCeilingZone(actualIndex)}
                            >
                              <Trash2 size={16} />
                              {t('shared.remove')}
                            </button>
                          </div>
                        </div>

                        {/* Ceiling Panel Color */}
                        <div className="form-section">
                          <h3 className="section-title">{t('ceiling.panelColor')}</h3>
                          <div className="color-selector with-images">
                            <button
                              type="button"
                              className={`icon-select-btn ${zone.color === 'White' ? 'selected' : ''}`}
                              onClick={() => handleZoneChange(actualIndex, 'color', 'White')}
                            >
                              <div className="icon-select-img">
                                <img src={WALL_PANEL_IMAGES.White.thumbnail} alt={t('common:productAlt.wcb', { color: t('common:colors.White') })} className="product-image" />
                              </div>
                              <span className="icon-select-label">{t('common:colors.White')}</span>
                            </button>
                            <button
                              type="button"
                              className={`icon-select-btn ${zone.color === 'Gray' ? 'selected' : ''}`}
                              onClick={() => handleZoneChange(actualIndex, 'color', 'Gray')}
                            >
                              <div className="icon-select-img">
                                <img src={WALL_PANEL_IMAGES.Gray.thumbnail} alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })} className="product-image" />
                              </div>
                              <span className="icon-select-label">{t('common:colors.Gray')}</span>
                            </button>
                          </div>
                        </div>

                        {/* Ceiling Dimensions */}
                        <div className="form-section">
                          <h3 className="section-title">{t('ceiling.dimensions')}</h3>

                          {/* Preset vs Custom Toggle */}
                          <div className="mode-toggle">
                            <button
                              type="button"
                              className={`mode-button ${zoneMode === 'preset' ? 'active' : ''}`}
                              onClick={() => handleZoneModeChange(zone.id, 'preset')}
                            >
                              <LayoutTemplate size={16} />
                              {t('shared.usePresets')}
                            </button>
                            <button
                              type="button"
                              className={`mode-button ${zoneMode === 'custom' ? 'active' : ''}`}
                              onClick={() => handleZoneModeChange(zone.id, 'custom')}
                            >
                              <Pencil size={16} />
                              {t('shared.customDimensions')}
                            </button>
                          </div>

                          {/* Preset Selection */}
                          {zoneMode === 'preset' && (
                            <div className="presets-grid" style={{ marginTop: '16px' }}>
                              {CEILING_PRESETS.map(preset => (
                                <button
                                  key={preset.id}
                                  type="button"
                                  className={`preset-card ${
                                    zone.length === preset.length && zone.width === preset.width ? 'selected' : ''
                                  }`}
                                  onClick={() => handleZonePresetSelect(actualIndex, preset.id)}
                                >
                                  <Maximize size={20} className="preset-icon" />
                                  <span className="preset-name">{t('ceiling.presetSize', { length: preset.length, width: preset.width })}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Custom Dimensions */}
                          {zoneMode === 'custom' && (
                            <>
                            <ZoneDimensionFields
                              unitKey={`ceiling-zone-${actualIndex}`}
                              length={zone.length}
                              width={zone.width}
                              onChange={(f, v) => handleZoneChange(actualIndex, f, v)}
                              lengthFieldId={`zone-${actualIndex}-length`}
                              widthFieldId={`zone-${actualIndex}-width`}
                            />

                            {/* Ceiling joists measurement guide */}
                            <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
                              <div className="flex-shrink-0 text-info">
                                <Info size={20} />
                              </div>
                              <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                                <p className="mb-0 small">
                                  {t('shared.joistNote')}
                                </p>
                                <div className="text-center flex-shrink-0">
                                  <img
                                    src={LEGACY_UI_ICONS.dimensionsGuide}
                                    alt={t('shared.joistImageAlt')}
                                    style={{ maxWidth: '200px', cursor: 'pointer' }}
                                    onClick={() => setShowLightbox(true)}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                  <span className="d-block text-muted mt-1" style={{ cursor: 'pointer', fontSize: '0.65rem' }} onClick={() => setShowLightbox(true)}>
                                    {t('shared.clickFullSize')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            </>
                          )}
                        </div>

                        {/* Zone Area Summary */}
                        <div className="zone-area-summary" style={{ marginTop: '16px' }}>
                          <Square size={16} />
                          <span>{t('ceiling.area')} <strong>{t('shared.squareFeet', { value: (zone.length * zone.width).toLocaleString() })}</strong></span>
                        </div>

                        {/* 3D Ceiling Viewer */}
                        <div className="zone-diagram" style={{ marginTop: '16px' }}>
                          <Ceiling3DViewer
                            length={zone.length}
                            width={zone.width}
                            color={zone.color}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Another Zone Button */}
              <button type="button" className="add-zone-button" onClick={() => { addCeilingZone(); trackZoneAdded((ceilingConfig.zones?.length || 0) + 1); }} style={{ marginTop: '16px' }}>
                <Plus size={20} />
                {t('ceiling.addZone')}
              </button>
            </div>
          </>
        )}

        {/* Info Box */}
        <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
          <div className="flex-shrink-0 text-info">
            <Info size={20} />
          </div>
          <div>
            <strong>{t('ceiling.whyTitle')}</strong>
            <p className="mb-0 small mt-1">
              {t('ceiling.whyBody')}
            </p>
          </div>
        </div>

        {showLightbox && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center lightbox-overlay"
            style={{ zIndex: 9999, cursor: 'pointer' }}
            onClick={() => setShowLightbox(false)}
          >
            <img src={LEGACY_UI_ICONS.dimensionsGuide} alt={t('shared.joistImageAlt')} className="lightbox-image" />
          </div>
        )}
      </div>
    );
  }

  // Standard mode (FullRoom): Image-based toggle + color selection
  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">{t('ceiling.title')}</h2>
        <p className="step-description">
          {t('ceiling.descriptionStandard')}
        </p>
      </div>

      <div className="row g-4 justify-content-center">
        {/* Ceiling Included - Image-based selector */}
        <div className="col-12 col-lg-auto">
          <div className="form-section">
            <h3 className="section-title">{t('ceiling.included')}</h3>
            <div className="ceiling-image-selector">
              <button
                type="button"
                className={`icon-select-btn ${!ceilingConfig.include ? 'selected' : ''}`}
                onClick={() => handleCeilingIncludeChange(false)}
              >
                <div className="icon-select-img">
                  <img
                    src={LEGACY_UI_ICONS.wallsOnly}
                    alt={t('ceiling.wallsOnlyAlt')}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <span className="icon-select-label">{t('shared.no')}</span>
              </button>

              <button
                type="button"
                className={`icon-select-btn ${ceilingConfig.include ? 'selected' : ''}`}
                onClick={() => handleCeilingIncludeChange(true)}
              >
                <div className="icon-select-img">
                  <img
                    src={LEGACY_UI_ICONS.wallsAndCeiling}
                    alt={t('ceiling.wallsAndCeilingAlt')}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <span className="icon-select-label">{t('shared.yes')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Vertical divider — visible only when side-by-side */}
        {ceilingConfig.include && (
          <div className="col-auto d-none d-lg-flex align-items-center px-0">
            <div style={{ width: '1px', height: '75%', backgroundColor: '#dee2e6' }}></div>
          </div>
        )}

        {/* Ceiling Color Selection (only if ceiling is included) */}
        {ceilingConfig.include && (
          <div className="col-12 col-lg-auto">
            <div className="form-section">
              <h3 className="section-title">{t('ceiling.panelColor')}</h3>
              <div className="color-selector with-images">
                <button
                  type="button"
                  className={`icon-select-btn ${ceilingConfig.color === 'White' ? 'selected' : ''}`}
                  onClick={() => handleColorChange('White')}
                >
                  <div className="icon-select-img">
                    <img
                      src={WALL_PANEL_IMAGES.White.thumbnail}
                      alt={t('common:productAlt.wcb', { color: t('common:colors.White') })}
                      className="product-image"
                    />
                  </div>
                  <span className="icon-select-label">{t('common:colors.White')}</span>
                </button>
                <button
                  type="button"
                  className={`icon-select-btn ${ceilingConfig.color === 'Gray' ? 'selected' : ''}`}
                  onClick={() => handleColorChange('Gray')}
                >
                  <div className="icon-select-img">
                    <img
                      src={WALL_PANEL_IMAGES.Gray.thumbnail}
                      alt={t('common:productAlt.wcb', { color: t('common:colors.Gray') })}
                      className="product-image"
                    />
                  </div>
                  <span className="icon-select-label">{t('common:colors.Gray')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
        <div className="flex-shrink-0 text-info">
          <Info size={20} />
        </div>
        <div>
          <strong>{t('ceiling.whyTitle')}</strong>
          <p className="mb-0 small mt-1">
            {t('ceiling.whyBody')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CeilingStep;
